import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { ContentFile, apiService } from '../../../services/api'
import { ContentAST } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'

interface UseChapterPersistenceArgs {
  bookId?: string
  currentChapter: ContentFile | null
  currentContent: ContentAST | null
  readOnly: boolean
  setCurrentChapter: Dispatch<SetStateAction<ContentFile | null>>
  setCurrentContent: Dispatch<SetStateAction<ContentAST | null>>
  setOriginalContent: Dispatch<SetStateAction<ContentAST | null>>
  /** Flush Lexical debounced state before save (e.g. editorRef.flushPendingSyncToParent) */
  prepareContentForAutoSave?: () => ContentAST | null
  /**
   * Called after a successful save with what was written. The book-wide views
   * (tag usage, sections TOC) rebuild from this, so they need the saved AST to
   * decide whether anything they derive actually moved.
   */
  onAfterSuccessfulSave?: (filename: string, saved: ContentAST) => void
}

const DIRTY_AUTOSAVE_MS = 3_000

/** Consecutive failed background saves before the author is told. One blip is noise. */
const FAILURES_BEFORE_ALERT = 2
const SAVE_FAILURE_TOAST_ID = 'background-save-failing'

export function useChapterPersistence({
  bookId,
  currentChapter,
  currentContent,
  readOnly,
  setCurrentChapter,
  setCurrentContent,
  setOriginalContent,
  prepareContentForAutoSave,
  onAfterSuccessfulSave,
}: UseChapterPersistenceArgs) {
  const [saving, setSaving] = useState(false)
  const [autoSaving, setAutoSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showSavedIndicator, setShowSavedIndicator] = useState(false)

  const hasUnsavedChangesRef = useRef(hasUnsavedChanges)
  hasUnsavedChangesRef.current = hasUnsavedChanges
  const currentChapterRef = useRef(currentChapter)
  currentChapterRef.current = currentChapter
  const savedIndicatorTimeoutRef = useRef<number | null>(null)
  // Bumped on every edit. A save carries a *snapshot*, so comparing the counter
  // at save-start with the counter when it lands tells us whether the author
  // typed while the request was in flight — see resetDirtyStateAfterSave.
  const editSeqRef = useRef(0)
  const consecutiveSaveFailuresRef = useRef(0)

  /**
   * A background save that fails is otherwise invisible: the author keeps typing
   * into a tab whose changes are going nowhere. Surface it after the second
   * consecutive failure and keep it up until a save lands.
   */
  const noteSaveOutcome = useCallback((succeeded: boolean) => {
    if (succeeded) {
      consecutiveSaveFailuresRef.current = 0
      toast.dismiss(SAVE_FAILURE_TOAST_ID)
      return
    }
    consecutiveSaveFailuresRef.current += 1
    if (consecutiveSaveFailuresRef.current < FAILURES_BEFORE_ALERT) {
      return
    }
    toast.error("Not saving — the server isn't responding. Your changes are still here; keep this tab open.", {
      id: SAVE_FAILURE_TOAST_ID,
      duration: Infinity,
    })
  }, [])

  const triggerSavedIndicator = useCallback(() => {
    setShowSavedIndicator(true)
    if (savedIndicatorTimeoutRef.current) {
      window.clearTimeout(savedIndicatorTimeoutRef.current)
    }
    savedIndicatorTimeoutRef.current = window.setTimeout(() => {
      setShowSavedIndicator(false)
      savedIndicatorTimeoutRef.current = null
    }, 1500)
  }, [])

  const resetDirtyStateAfterSave = useCallback(
    async (
      content: ContentAST,
      options: {
        showSavedIndicator?: boolean
        /** editSeqRef at the moment `content` was snapshotted, from the caller. */
        editSeq?: number
      } = {},
    ) => {
      // A background save (tab switch) can resolve after the user moved to another
      // file — don't reset that file's dirty state or flash its saved indicator.
      const stillCurrent = currentChapterRef.current?.filename === currentChapter?.filename
      // Keystrokes typed while the request was in flight are not in the snapshot
      // that just landed. Clearing the flag for them would mark them saved, so the
      // autosave loop would never retry and the revalidation path — which only
      // defers to local state while it is dirty — would overwrite them on the next
      // window focus. Stay dirty and let the next tick save the newer document.
      const editedDuringSave = options.editSeq != null && options.editSeq !== editSeqRef.current
      if (stillCurrent) {
        setOriginalContent(content)
        if (!editedDuringSave) {
          setHasUnsavedChanges(false)
        }
      }

      if (options.showSavedIndicator && stillCurrent) {
        triggerSavedIndicator()
      }
    },
    [currentChapter?.filename, setOriginalContent, triggerSavedIndicator],
  )

  const saveChapter = useCallback(
    async (trigger: 'manual' | 'switch' | 'autosave', contentOverride?: ContentAST | null) => {
      const effectiveContent = contentOverride ?? currentContent
      if (!currentChapter || !bookId || !effectiveContent) {
        return null
      }
      const response = await apiService.updateChapter(bookId, currentChapter.filename, {
        authorContent: effectiveContent,
      })

      // Adopt the revision this write produced. Skip it and the cached copy looks
      // stale to the next revalidation, which then refetches the whole file to
      // rediscover content it already has.
      if (response.data?.revision) {
        const savedFilename = currentChapter.filename
        setCurrentChapter((prev) =>
          prev && prev.filename === savedFilename ? { ...prev, revision: response.data.revision } : prev,
        )
      }
      return response
    },
    [bookId, currentChapter, currentContent, setCurrentChapter],
  )

  const handleAutoSave = useCallback(async () => {
    const flushed = prepareContentForAutoSave?.() ?? null
    const contentToSave = flushed ?? currentContent
    // After the flush: flushing hands the editor's pending edits over, and those
    // count as part of this snapshot rather than as edits made during the save.
    const editSeq = editSeqRef.current
    const shouldSave = flushed != null || hasUnsavedChangesRef.current
    if (!currentChapter || !bookId || !contentToSave || !shouldSave) {
      return
    }

    try {
      setAutoSaving(true)
      const response = await saveChapter('autosave', contentToSave)
      if (!response) {
        return
      }

      if (response.success) {
        noteSaveOutcome(true)
        await resetDirtyStateAfterSave(contentToSave, { editSeq })
        onAfterSuccessfulSave?.(currentChapter.filename, contentToSave)
      } else {
        noteSaveOutcome(false)
        logger.warn('Auto-save failed:', response.message)
      }
    } catch (err: any) {
      noteSaveOutcome(false)
      logger.error('Auto-save error:', err.response?.data?.message || err.message)
    } finally {
      setAutoSaving(false)
    }
  }, [
    bookId,
    currentChapter,
    currentContent,
    noteSaveOutcome,
    onAfterSuccessfulSave,
    prepareContentForAutoSave,
    resetDirtyStateAfterSave,
    saveChapter,
  ])

  const persistCurrentChapterBeforeNavigation = useCallback(async (): Promise<boolean> => {
    const flushed = prepareContentForAutoSave?.() ?? null
    const contentToSave = flushed ?? currentContent
    const editSeq = editSeqRef.current
    if ((!hasUnsavedChangesRef.current && !flushed) || !currentChapter || !contentToSave) {
      return true
    }

    try {
      const response = await saveChapter('switch', contentToSave)
      if (!response) {
        noteSaveOutcome(false)
        toast.error('Could not auto-save current chapter')
        return false
      }
      noteSaveOutcome(true)
      await resetDirtyStateAfterSave(contentToSave, {
        showSavedIndicator: true,
        editSeq,
      })
      onAfterSuccessfulSave?.(currentChapter.filename, contentToSave)
      return true
    } catch (err: any) {
      noteSaveOutcome(false)
      logger.error('Auto-save before chapter navigation failed:', err?.response?.data?.message || err?.message || err)
      toast.error('Could not auto-save current chapter')
      return false
    }
  }, [
    currentChapter,
    currentContent,
    noteSaveOutcome,
    onAfterSuccessfulSave,
    prepareContentForAutoSave,
    resetDirtyStateAfterSave,
    saveChapter,
  ])

  const handleContentChange = useCallback(
    (ast: ContentAST) => {
      if (readOnly) {
        return
      }
      editSeqRef.current += 1
      setCurrentContent(ast)
      setHasUnsavedChanges(true)
    },
    [readOnly, setCurrentContent],
  )

  /**
   * `contentFromEditor` is the AST the editor hands over with the save request.
   * Without it a save fired mid-typing writes stale content: the editor syncs to
   * React state on a 150ms debounce, and flushing that debounce here only queues
   * a state update — `currentContent` in this closure is still the pre-flush
   * value. The save would then persist the older document *and* clear the dirty
   * flag, so the newest edits never reach the server at all.
   */
  const handleSave = useCallback(
    async (contentFromEditor?: ContentAST) => {
      const flushed = contentFromEditor ?? prepareContentForAutoSave?.() ?? null
      const contentToSave = flushed ?? currentContent
      const editSeq = editSeqRef.current
      if (!currentChapter || !bookId || !contentToSave) {
        return
      }

      try {
        setSaving(true)
        await saveChapter('manual', contentToSave)
        noteSaveOutcome(true)
        await resetDirtyStateAfterSave(contentToSave, {
          showSavedIndicator: true,
          editSeq,
        })
        onAfterSuccessfulSave?.(currentChapter.filename, contentToSave)
      } catch (err) {
        noteSaveOutcome(false)
        logger.error('Failed to save chapter:', err)
        toast.error('Failed to save chapter')
      } finally {
        setSaving(false)
      }
    },
    [
      bookId,
      currentChapter,
      currentContent,
      noteSaveOutcome,
      onAfterSuccessfulSave,
      prepareContentForAutoSave,
      resetDirtyStateAfterSave,
      saveChapter,
    ],
  )

  // Debounced autosave
  useEffect(() => {
    if (readOnly) {
      return
    }
    const timer = window.setInterval(() => {
      if (hasUnsavedChangesRef.current) {
        handleAutoSave()
      }
    }, DIRTY_AUTOSAVE_MS)
    return () => window.clearInterval(timer)
  }, [handleAutoSave, readOnly])

  // Save on visibility change, blur, pagehide, freeze
  useEffect(() => {
    const runAutoSave = () => {
      if (hasUnsavedChangesRef.current) {
        handleAutoSave()
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden && hasUnsavedChangesRef.current) {
        logger.debug('Triggering auto-save due to visibility change')
        runAutoSave()
      }
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        runAutoSave()
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    const handleWindowBlur = () => {
      if (hasUnsavedChangesRef.current) {
        runAutoSave()
      }
    }

    const handlePageHide = () => {
      if (hasUnsavedChangesRef.current) {
        logger.debug('Triggering auto-save due to pagehide')
        runAutoSave()
      }
    }

    const handleFreeze = () => {
      if (hasUnsavedChangesRef.current) {
        logger.debug('Triggering auto-save due to freeze')
        runAutoSave()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('freeze', handleFreeze)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('freeze', handleFreeze)
    }
  }, [handleAutoSave])

  useEffect(() => {
    return () => {
      if (savedIndicatorTimeoutRef.current) {
        window.clearTimeout(savedIndicatorTimeoutRef.current)
      }
    }
  }, [])

  return {
    saving,
    autoSaving,
    hasUnsavedChanges,
    showSavedIndicator,
    setHasUnsavedChanges,
    persistCurrentChapterBeforeNavigation,
    handleSave,
    handleContentChange,
  }
}
